import {
  DEFAULT_VIEW_SCHEMA_VERSION,
  type EmptyViewConfig,
  type ViewDefinition,
  type ViewDefinitionMap,
  type ViewDisplayMeta,
  type ViewInteractionPolicy,
  type ViewPatternKey,
  type ViewRole,
  type ViewType,
} from './views.ts';

export const ATOMIC_KEYS = [
  'label_text',
  'body_text',
  'metric_value',
  'status_badge',
  'trend_indicator',
  'icon_token',
  'timestamp_text',
  'entity_chip',
  'empty_illustration',
  'table_cell_text',
] as const;

export const MOLECULE_KEYS = [
  'document_header',
  'document_block',
  'metric_tile',
  'score_segment_bar',
  'action_row',
  'entity_header',
  'fact_list',
  'health_dimension_row',
  'table_header_row',
  'table_data_row',
  'chart_plot',
  'timeline_event',
  'empty_callout',
] as const;

export type AtomicKey = typeof ATOMIC_KEYS[number];
export type MoleculeKey = typeof MOLECULE_KEYS[number];
export type ViewDataContractKey = ViewType;

export interface ViewPatternTaxonomy {
  atoms: readonly AtomicKey[];
  molecules: readonly MoleculeKey[];
}

export interface CanonicalViewRegistryEntry<TPatternKey extends ViewPatternKey = ViewPatternKey> {
  patternKey: TPatternKey;
  sourceViewTypes: readonly ViewType[];
  envelopeViewType: ViewType;
  dataContractKey: ViewDataContractKey;
  fallbackPatternKey: 'fallback_empty';
  definitionTemplate: ViewDefinitionMap[TPatternKey];
  taxonomy: ViewPatternTaxonomy;
  supportsPrimaryAction: boolean;
  supportsItemAction: boolean;
  supportsCommunication: boolean;
}

type DefinitionOverrides = {
  title?: string;
  description?: string;
  sourceViewType?: ViewType | null;
};

const EMPTY_CONFIG: EmptyViewConfig = {};

const createInteractionPolicy = (params: {
  role: ViewRole;
  allowsPrimaryAction: boolean;
  supportsCommunication: boolean;
}): ViewInteractionPolicy => ({
  role: params.role,
  allowsRefresh: true,
  allowsPrimaryAction: params.allowsPrimaryAction,
  requiresConfirmation: false,
  supportsCommunication: params.supportsCommunication,
});

const buildDefinitionTemplate = <TPatternKey extends ViewPatternKey>(params: {
  patternKey: TPatternKey;
  role: ViewRole;
  sourceViewType: ViewType;
  title: string;
  description?: string;
  icon: NonNullable<ViewDisplayMeta['icon']>;
  allowsPrimaryAction?: boolean;
  supportsCommunication?: boolean;
}): ViewDefinitionMap[TPatternKey] => ({
  patternKey: params.patternKey,
  schemaVersion: DEFAULT_VIEW_SCHEMA_VERSION,
  sourceViewType: params.sourceViewType,
  config: EMPTY_CONFIG,
  displayMeta: {
    title: params.title,
    ...(params.description ? { description: params.description } : {}),
    icon: params.icon,
  },
  interactionPolicy: createInteractionPolicy({
    role: params.role,
    allowsPrimaryAction: params.allowsPrimaryAction ?? false,
    supportsCommunication: params.supportsCommunication ?? false,
  }),
  fallbackPatternKey: 'fallback_empty',
}) as ViewDefinitionMap[TPatternKey];

const buildRegistryEntry = <TPatternKey extends ViewPatternKey>(params: {
  patternKey: TPatternKey;
  sourceViewTypes: readonly ViewType[];
  envelopeViewType: ViewType;
  definitionTemplate: ViewDefinitionMap[TPatternKey];
  taxonomy: ViewPatternTaxonomy;
  supportsPrimaryAction: boolean;
  supportsItemAction: boolean;
  supportsCommunication: boolean;
}): CanonicalViewRegistryEntry<TPatternKey> => ({
  patternKey: params.patternKey,
  sourceViewTypes: params.sourceViewTypes,
  envelopeViewType: params.envelopeViewType,
  dataContractKey: params.envelopeViewType,
  fallbackPatternKey: 'fallback_empty',
  definitionTemplate: params.definitionTemplate,
  taxonomy: params.taxonomy,
  supportsPrimaryAction: params.supportsPrimaryAction,
  supportsItemAction: params.supportsItemAction,
  supportsCommunication: params.supportsCommunication,
});

export const CANONICAL_VIEW_REGISTRY = [
  buildRegistryEntry({
    patternKey: 'artifact_document',
    sourceViewTypes: ['artifact_document'],
    envelopeViewType: 'artifact_document',
    definitionTemplate: buildDefinitionTemplate({
      patternKey: 'artifact_document',
      role: 'informative',
      sourceViewType: 'artifact_document',
      title: 'Artifact Document',
      icon: 'document',
      allowsPrimaryAction: true,
      supportsCommunication: false,
    }),
    taxonomy: {
      atoms: ['label_text', 'body_text', 'timestamp_text', 'icon_token'],
      molecules: ['document_header', 'document_block'],
    },
    supportsPrimaryAction: true,
    supportsItemAction: false,
    supportsCommunication: false,
  }),
  buildRegistryEntry({
    patternKey: 'score_card',
    sourceViewTypes: ['score_card'],
    envelopeViewType: 'score_card',
    definitionTemplate: buildDefinitionTemplate({
      patternKey: 'score_card',
      role: 'informative',
      sourceViewType: 'score_card',
      title: 'Score Card',
      icon: 'score',
      allowsPrimaryAction: true,
      supportsCommunication: true,
    }),
    taxonomy: {
      atoms: ['label_text', 'metric_value', 'status_badge', 'trend_indicator'],
      molecules: ['metric_tile', 'score_segment_bar'],
    },
    supportsPrimaryAction: true,
    supportsItemAction: false,
    supportsCommunication: true,
  }),
  buildRegistryEntry({
    patternKey: 'action_list',
    sourceViewTypes: ['action_list'],
    envelopeViewType: 'action_list',
    definitionTemplate: buildDefinitionTemplate({
      patternKey: 'action_list',
      role: 'actionable',
      sourceViewType: 'action_list',
      title: 'Action List',
      icon: 'actions',
      allowsPrimaryAction: true,
      supportsCommunication: true,
    }),
    taxonomy: {
      atoms: ['label_text', 'body_text', 'timestamp_text', 'entity_chip', 'status_badge'],
      molecules: ['action_row', 'empty_callout'],
    },
    supportsPrimaryAction: true,
    supportsItemAction: true,
    supportsCommunication: true,
  }),
  buildRegistryEntry({
    patternKey: 'metric_grid',
    sourceViewTypes: ['metric_grid'],
    envelopeViewType: 'metric_grid',
    definitionTemplate: buildDefinitionTemplate({
      patternKey: 'metric_grid',
      role: 'informative',
      sourceViewType: 'metric_grid',
      title: 'Metric Grid',
      icon: 'metrics',
    }),
    taxonomy: {
      atoms: ['label_text', 'metric_value', 'status_badge', 'trend_indicator'],
      molecules: ['metric_tile', 'empty_callout'],
    },
    supportsPrimaryAction: false,
    supportsItemAction: false,
    supportsCommunication: false,
  }),
  buildRegistryEntry({
    patternKey: 'health_gauge',
    sourceViewTypes: ['health_gauge'],
    envelopeViewType: 'health_gauge',
    definitionTemplate: buildDefinitionTemplate({
      patternKey: 'health_gauge',
      role: 'informative',
      sourceViewType: 'health_gauge',
      title: 'Health Gauge',
      icon: 'health',
      allowsPrimaryAction: true,
      supportsCommunication: true,
    }),
    taxonomy: {
      atoms: ['label_text', 'body_text', 'status_badge'],
      molecules: ['entity_header', 'health_dimension_row'],
    },
    supportsPrimaryAction: true,
    supportsItemAction: false,
    supportsCommunication: true,
  }),
  buildRegistryEntry({
    patternKey: 'entity_summary',
    sourceViewTypes: ['entity_summary'],
    envelopeViewType: 'entity_summary',
    definitionTemplate: buildDefinitionTemplate({
      patternKey: 'entity_summary',
      role: 'informative',
      sourceViewType: 'entity_summary',
      title: 'Entity Summary',
      icon: 'entity',
      supportsCommunication: true,
    }),
    taxonomy: {
      atoms: ['label_text', 'body_text', 'status_badge', 'entity_chip', 'timestamp_text'],
      molecules: ['entity_header', 'fact_list'],
    },
    supportsPrimaryAction: false,
    supportsItemAction: false,
    supportsCommunication: true,
  }),
  buildRegistryEntry({
    patternKey: 'data_table',
    sourceViewTypes: ['data_table'],
    envelopeViewType: 'data_table',
    definitionTemplate: buildDefinitionTemplate({
      patternKey: 'data_table',
      role: 'informative',
      sourceViewType: 'data_table',
      title: 'Data Table',
      icon: 'table',
    }),
    taxonomy: {
      atoms: ['label_text', 'table_cell_text'],
      molecules: ['table_header_row', 'table_data_row', 'empty_callout'],
    },
    supportsPrimaryAction: false,
    supportsItemAction: false,
    supportsCommunication: false,
  }),
  buildRegistryEntry({
    patternKey: 'chart',
    sourceViewTypes: ['chart'],
    envelopeViewType: 'chart',
    definitionTemplate: buildDefinitionTemplate({
      patternKey: 'chart',
      role: 'informative',
      sourceViewType: 'chart',
      title: 'Chart',
      icon: 'chart',
    }),
    taxonomy: {
      atoms: ['label_text', 'metric_value', 'body_text'],
      molecules: ['chart_plot', 'empty_callout'],
    },
    supportsPrimaryAction: false,
    supportsItemAction: false,
    supportsCommunication: false,
  }),
  buildRegistryEntry({
    patternKey: 'timeline',
    sourceViewTypes: ['timeline'],
    envelopeViewType: 'timeline',
    definitionTemplate: buildDefinitionTemplate({
      patternKey: 'timeline',
      role: 'informative',
      sourceViewType: 'timeline',
      title: 'Timeline',
      icon: 'timeline',
    }),
    taxonomy: {
      atoms: ['label_text', 'body_text', 'status_badge'],
      molecules: ['timeline_event', 'empty_callout'],
    },
    supportsPrimaryAction: false,
    supportsItemAction: false,
    supportsCommunication: false,
  }),
  buildRegistryEntry({
    patternKey: 'fallback_empty',
    sourceViewTypes: ['fallback_panel'],
    envelopeViewType: 'fallback_panel',
    definitionTemplate: buildDefinitionTemplate({
      patternKey: 'fallback_empty',
      role: 'informative',
      sourceViewType: 'fallback_panel',
      title: 'Fallback Empty',
      icon: 'empty',
    }),
    taxonomy: {
      atoms: ['label_text', 'body_text', 'empty_illustration'],
      molecules: ['empty_callout'],
    },
    supportsPrimaryAction: false,
    supportsItemAction: false,
    supportsCommunication: false,
  }),
] as const satisfies readonly CanonicalViewRegistryEntry[];

const REGISTRY_BY_PATTERN = Object.fromEntries(
  CANONICAL_VIEW_REGISTRY.map((entry) => [entry.patternKey, entry]),
) as {
  [K in ViewPatternKey]: Extract<typeof CANONICAL_VIEW_REGISTRY[number], { patternKey: K }>;
};

const PATTERN_BY_SOURCE_VIEW_TYPE = Object.freeze({
  artifact_document: 'artifact_document',
  score_card: 'score_card',
  action_list: 'action_list',
  metric_grid: 'metric_grid',
  health_gauge: 'health_gauge',
  entity_summary: 'entity_summary',
  data_table: 'data_table',
  chart: 'chart',
  timeline: 'timeline',
  fallback_panel: 'fallback_empty',
} as const satisfies Record<ViewType, ViewPatternKey>);

export const getCanonicalViewRegistryEntry = <TPatternKey extends ViewPatternKey>(
  patternKey: TPatternKey,
): Extract<typeof CANONICAL_VIEW_REGISTRY[number], { patternKey: TPatternKey }> => REGISTRY_BY_PATTERN[patternKey];

export const getPatternKeyForSourceViewType = (
  viewType: ViewType,
): ViewPatternKey => PATTERN_BY_SOURCE_VIEW_TYPE[viewType];

export const createCanonicalViewDefinition = <TPatternKey extends ViewPatternKey>(
  patternKey: TPatternKey,
  params?: DefinitionOverrides,
): ViewDefinitionMap[TPatternKey] => {
  const template = getCanonicalViewRegistryEntry(patternKey).definitionTemplate;

  return {
    ...template,
    sourceViewType: params?.sourceViewType === undefined
      ? template.sourceViewType
      : params.sourceViewType,
    displayMeta: {
      ...template.displayMeta,
      ...(params?.title ? { title: params.title } : {}),
      ...(params?.description === undefined
        ? {}
        : { description: params.description }),
    },
    interactionPolicy: {
      ...template.interactionPolicy,
    },
    config: {
      ...template.config,
    },
  } as ViewDefinitionMap[TPatternKey];
};

export const createViewDefinitionForViewType = (
  viewType: ViewType,
  params?: Omit<DefinitionOverrides, 'sourceViewType'>,
): ViewDefinition => {
  const patternKey = getPatternKeyForSourceViewType(viewType);
  return createCanonicalViewDefinition(patternKey, {
    ...params,
    sourceViewType: viewType,
  });
};

export const getCanonicalViewDefinition = (
  patternKey: ViewPatternKey,
): ViewDefinition => createCanonicalViewDefinition(patternKey);
